import { Modal } from "@material-ui/core";
import { PromptModal } from "components/modal/promptModal.component";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import { TimeReportForStamping, TimeReportsForStamping } from "model/Company";
import { StampClockTeam } from "model/OrganizationalStructure";
import { TimeReportDto } from "model/TimeReport";
import moment from "moment-timezone";
import "moment/locale/sv";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-tooltip/dist/react-tooltip.css";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  CompanyNameContainer,
  DayInfoContainer,
  FormContainer,
  FormStyled,
  InfoContainer,
  InputContainer,
  Text,
  TextInput,
} from "./clockStamp.styled";
import { ClockStampCheckInOutModal } from "./clockStampCheckInOutModal.component";
import { ClockStampChooseTimeReportModal } from "./clockStampChooseTimeReportModal.component";
import { ClockStampCreateAutoGeneratedGigModal } from "./clockStampCreateAutoGeneratedGigModal.component";
import { ClockStampTimeReportModal } from "./clockStampTimeReportModal.component";
import { NumberPad } from "./numberPad.component";
import { CustomIcon } from "components/icon/customIcon.component";

export const ClockStamp = () => {
  const { t, i18n } = useTranslation();
  const [telephoneNumber, setTelephoneNumber] = useState<string>("+46");
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const [companyState, companyActions] = useCompanyStore();
  const numberRegex = /^\+46\d{9}$/;

  const [userIsCheckedIn, setUserIsCheckedIn] = useState<boolean>(false);
  const [keyShouldBeDisabled, setKeyShouldBeDisabled] =
    useState<boolean>(false);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [today, setToday] = useState<string>("");
  const [time, setTime] = useState<string>("");

  const [timeReport, setTimeReport] = useState<TimeReportForStamping>();
  const [workerTimeReports, setWorkerTimeReports] =
    useState<TimeReportsForStamping | null>(null);

  const [errorText, setErrorText] = useState("");

  const [showCreateAutoGeneratedGigModal, setShowCreateAutoGeneratedGigModal] =
    useState(false);
  const [isAutoGeneratedGig, setIsAutoGeneratedGig] = useState(false);

  const [showTimeReportModal, setShowTimeReportModal] = useState(false);
  const [timeReportToTimeReport, setTimeReportToTimeReport] =
    useState<TimeReportDto>();

  const [teams, setTeams] = useState<StampClockTeam[]>([]);
  const [showChooseTimeReportModal, setShowChooseTimeReportModal] =
    useState(false);

  const [showWarningModal, setShowWarningModal] = useState(false);

  useEffect(() => {
    updateTime();
    const intervalId = setInterval(updateTime, 60000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isValidNumber = numberRegex.test(telephoneNumber);

    if (telephoneNumber.length >= 2) {
      setErrorText("");
    }

    if (isValidNumber) {
      setErrorText("");
      getWorkerTimeReport();
    }
  }, [telephoneNumber, companyState.company?.id]);

  useEffect(() => {
    if (workerTimeReports) {
      if (workerTimeReports.timeReports.length > 1) {
        setTimeReport(undefined);
        setShowChooseTimeReportModal(true);
      } else {
        const tr = workerTimeReports.timeReports[0];
        const tExt: TimeReportForStamping = {
          ...tr,
          companyId: workerTimeReports.companyId,
          workerPhoneNumber: workerTimeReports.workerPhoneNumber,
        };
        setTimeReport(tExt);
      }
    }
  }, [workerTimeReports]);

  useEffect(() => {
    if (timeReportToTimeReport) {
      setShowTimeReportModal(true);
    }
  }, [timeReportToTimeReport]);

  useEffect(() => {
    if (timeReport) {
      setUserIsCheckedIn(
        !!(timeReport.checkInTime && !timeReport.checkOutTime)
      );
    } else {
      setUserIsCheckedIn(false);
    }
    setKeyShouldBeDisabled(numberRegex.test(telephoneNumber) && !!timeReport);
  }, [timeReport, telephoneNumber]);

  const updateTime = () => {
    const lang = (i18n.language || "sv").split("-")[0];

    const currentDate = moment().tz("Europe/Stockholm").locale(lang);

    setTime(currentDate.format("HH:mm"));
    setToday(
      currentDate.format("dddd D MMMM").replace(/^\w/, (c) => c.toUpperCase())
    );
  };

  const getWorkerTimeReport = async () => {
    const phoneSnapshot = (telephoneNumber || "").trim();
    const isValidNumber = numberRegex.test(phoneSnapshot);
    const companyId = companyState.company?.id;
    if (!isValidNumber || !companyId) return;

    setPhoneNumber("");

    setShowChooseTimeReportModal(false);
    setShowModal(false);
    setIsAutoGeneratedGig(false);
    setTimeReport(undefined);
    setWorkerTimeReports(null);
    setUserIsCheckedIn(false);

    let shouldOpenAutoGig = false;

    try {
      const res = await companyActions.getAvailableTimeReportsForStamping(
        companyId,
        phoneSnapshot.substring(1)
      );
      setWorkerTimeReports(res);
    } catch (err: any) {
      const msg = err?.response?.data?.errorMessage;

      if (msg === "No matching time report found") {
        try {
          const [allowGenerateRes, teamsRes] = await Promise.all([
            Api().company.getSettingForAllowingAutoGeneratedGig(companyId),
            Api().company.getStampClockWorkerWorkgroups(
              companyId,
              phoneSnapshot.substring(1)
            ),
          ]);
          setTeams(teamsRes.data);
          if (allowGenerateRes.data && teamsRes.data.length >= 1) {
            shouldOpenAutoGig = true;
          } else {
            setErrorText(t("StampClock.NoOpenTimeReports"));
          }
        } catch (e) {
          console.error("Failed to check auto-generate setting:", e);
          setErrorText(t("StampClock.NoOpenTimeReports"));
        }
      } else if (msg === "Worker not found") {
        setErrorText(t("StampClock.NoNumber"));
      } else if (msg === "Invalid or missing ssn") {
        setErrorText("StampClock.InvalidOrMissingSSN");
      } else {
        setErrorText(t("StampClock.NoOpenTimeReports"));
      }
    } finally {
      setPhoneNumber(phoneSnapshot);

      if (shouldOpenAutoGig) {
        setShowCreateAutoGeneratedGigModal(true);
      }
    }
  };

  const clearInputs = () => {
    setTelephoneNumber("+46");
    setErrorText("");
    setTimeReport(undefined);
    setKeyShouldBeDisabled(false);
  };

  const onClickedC = () => {
    setTelephoneNumber("+46");
    setErrorText("");
    setKeyShouldBeDisabled(false);
  };

  const handleCheckInCheckOut = () => {
    setShowChooseTimeReportModal(false);
    if (!timeReport) return;

    const {
      timeReportId,
      checkInTime,
      checkOutTime,
      companyId,
      workerPhoneNumber,
    } = timeReport;

    const phoneOk = workerPhoneNumber === telephoneNumber.substring(1);
    const hasOpenSide = !checkInTime || !checkOutTime;
    if (!timeReportId || !companyId || !phoneOk || !hasOpenSide) return;

    doTheCheckInAndOut(companyId, timeReportId, workerPhoneNumber);
  };

  const doTheCheckInAndOut = (
    companyId: number,
    timeReportId: number,
    workerPhoneNumber: string
  ) => {
    companyActions
      .handleCheckInCheckOut(companyId, timeReportId, workerPhoneNumber)
      .then(() => {
        setShowModal(true);
        setTelephoneNumber("+46");
        setKeyShouldBeDisabled(false);
      })
      .catch(() => {
        console.log("problem checkin and out");
      });
  };

  const handleSubmitTimeReport = () => {
    setShowModal(false);
    if (
      timeReport?.timeReportId &&
      timeReport?.workerPhoneNumber &&
      companyState.company?.id
    ) {
      Api()
        .company.getStampClockTimeReport(
          companyState.company.id,
          timeReport.workerPhoneNumber,
          timeReport.timeReportId
        )
        .then((res) => {
          setTimeReportToTimeReport(res.data);
        })
        .catch((err: any) => {
          console.error("Error fetching time report:", err);
        });
    }
  };

  const haveStampClockModule = useMemo(() => {
    return (
      companyState.companySettings?.find(
        (s: any) => s.key === "CompanyShowStampclockModule"
      )?.value == "true"
    );
  }, [companyState.companySettings]);

  if (!haveStampClockModule)
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          width: "100%",
        }}
      >
        <CustomIcon
          name="clock"
          color={Color.BurntSienna}
          size="100px"
          style={{ marginTop: 150 }}
        />
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          fontSize={FontSize.H3}
          align="center"
          color={Color.MidnightBlue}
        >
          {t("StampClock.Oops")}
        </Text>
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          fontSize={FontSize.H4}
          align="center"
          color={Color.MidnightBlue}
        >
          {t("StampClock.ModuleInactive")}
        </Text>
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          fontSize={FontSize.H4}
          align="center"
          color={Color.MidnightBlue}
        >
          {t("StampClock.YourAdminCanActivate")}
        </Text>
      </div>
    );

  return (
    <>
      <InfoContainer>
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          fontSize={FontSize.H2}
          color={Color.MidnightBlue}
          alignSelf={"flex-start"}
          style={{ marginTop: 150 }}
        >
          {t("StampClock.Welcome")}
        </Text>
        <DayInfoContainer>
          <Text
            fontFamily={FontFamily.MontserratSemiBold}
            fontSize={FontSize.H2}
            color={Color.MidnightBlue}
          >
            {time}
          </Text>
          <Text
            fontFamily={FontFamily.MontserratSemiBold}
            fontSize={FontSize.H2}
            color={Color.MidnightBlue}
          >
            {today}
          </Text>
        </DayInfoContainer>
      </InfoContainer>

      <FormContainer>
        <FormStyled>
          {errorText !== "" && (
            <Text
              fontFamily={FontFamily.MontserratSemiBold}
              fontSize={FontSize.H4}
              color={Color.Destructive}
              marginLeft={70}
            >
              {t(errorText)}
            </Text>
          )}

          <InputContainer>
            <TextInput
              value={telephoneNumber}
              onChange={(e: any) => setTelephoneNumber(e.target.value)}
              placeholder={t("StampClock.TelephoneNumber")}
            />
          </InputContainer>

          <NumberPad
            checkedIn={userIsCheckedIn}
            keyShouldBeDisabled={keyShouldBeDisabled}
            onNumberChange={(newNumber) =>
              typeof newNumber === "boolean"
                ? onClickedC()
                : setTelephoneNumber(telephoneNumber + newNumber)
            }
            onClickStampKey={() => handleCheckInCheckOut()}
          />
        </FormStyled>

        <CompanyNameContainer>
          <Text fontSize={FontSize.H3}>
            {companyState.company?.companyName}
          </Text>
        </CompanyNameContainer>
      </FormContainer>

      {showModal && timeReport && (
        <ClockStampCheckInOutModal
          timeReport={timeReport}
          setShowModal={setShowModal}
          setKeyShouldBeDisabled={setKeyShouldBeDisabled}
          showModal={showModal}
          isAutoGeneratedGig={isAutoGeneratedGig}
          setIsAutoGeneratedGig={setIsAutoGeneratedGig}
          handleSubmitTimeReport={handleSubmitTimeReport}
          clearInputs={clearInputs}
        />
      )}

      {showCreateAutoGeneratedGigModal && (
        <ClockStampCreateAutoGeneratedGigModal
          showCreateAutoGeneratedGigModal={showCreateAutoGeneratedGigModal}
          setShowCreateAutoGeneratedGigModal={
            setShowCreateAutoGeneratedGigModal
          }
          setKeyShouldBeDisabled={setKeyShouldBeDisabled}
          setTimeReport={setTimeReport}
          setShowModal={setShowModal}
          setIsAutoGeneratedGig={setIsAutoGeneratedGig}
          clearInputs={clearInputs}
          teams={teams}
          phoneNumber={phoneNumber}
        />
      )}

      {showTimeReportModal && timeReportToTimeReport && (
        <ClockStampTimeReportModal
          showTimeReportModal={showTimeReportModal}
          setShowTimeReportModal={setShowTimeReportModal}
          timereport={timeReportToTimeReport}
          setTimeReportToTimeReport={setTimeReportToTimeReport}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          setKeyShouldBeDisabled={setKeyShouldBeDisabled}
          setShowWarningModal={setShowWarningModal}
        />
      )}

      {showChooseTimeReportModal && (
        <ClockStampChooseTimeReportModal
          setShowChooseTimeReportModal={setShowChooseTimeReportModal}
          setKeyShouldBeDisabled={setKeyShouldBeDisabled}
          showChooseTimeReportModal={showChooseTimeReportModal}
          setTimeReport={setTimeReport}
          clearInputs={clearInputs}
          workerTimeReports={workerTimeReports}
          handleCheckInCheckOut={() => handleCheckInCheckOut()}
          timeReport={timeReport}
        />
      )}
      <Modal
        open={showWarningModal}
        onClose={() => {
          setShowWarningModal(false);
        }}
      >
        <PromptModal
          header={t("StampClock.CancelTimeReporting")}
          message={t("StampClock.AreYourSureYouDontWantToTimeReport")}
          accept={{
            label: t("General.Yes"),
            color: Color.MidnightBlue,
            callback: () => {
              setShowTimeReportModal(false);
              setShowWarningModal(false);
            },
          }}
          cancel={{
            label: t("General.No"),
            color: Color.Destructive,
            callback: () => {
              setShowWarningModal(false);
            },
          }}
        />
      </Modal>
    </>
  );
};
